import React from "react";
import "../HumanPractices/Gallery.css";
import PersonalCard from "../HumanPractices/PersonalCard";

const mainTakeawaysCreus = [
  "Process of lyophilisation and encapsulation of Plusbiome product",
  "Regulation in the field of probiotics",
  "Cost and viability of our project",
];

const ArticleProduct = () => {
  return (
    <div className="mb-20">
      <div>
        <h2 id="Intro">From a project to a product</h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          For our team to be successful in producing a Live Biotherapeutic
          Product (LBP) to remove cadmium from the intestine and finally
          transform it into a product, we had to consider various aspects.
          <br></br>
          <br></br>
          We investigated four major topics:<br></br>
          <br></br>
          <div className="mr-10 ml-10">
          <ulb>
            <center>
              <li> How are commercial probiotics made</li>
              <li> Pharmaceutical approval </li>
              <li> Cost of our product</li>
              <li> Interest of the population</li>
            </center>
          </ulb>
          </div>

        </p>
        <br />
        <h2 className="mt-20" id="Discussions">
          {" "}
          Discussions with experts{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          We sought to gain insights into the current advancements in probiotics
          and live biological therapeutics, leading us to engage with various
          companies. We had the privilege of meeting with Marcus Creus, who
          serves as the CSO (chief scientific officer) of Plusbiome
          <a href="#refproduct1">
            <sup>1</sup>
          </a>
          , a Swiss startup specializing in probiotics located in Basel. Our
          conversation encompassed a wide range of topics including our project,
          the encapsulation procedure, bacterial processing, regulatory
          considerations, cost analysis, and ultimately, the project's
          feasibility.
          <br />
          <br />
          <PersonalCard
            photourl="https://static.igem.wiki/teams/4888/wiki/humanpractices/marcus-creus.jpeg"
            name="Marcus Creus, CSO of Plusbiome"
            introduction=<p>
              {" "}
              Plusbiome AG is a Swiss startup company working at the interface
              of digital health and the microbiome with the purpose of making
              microbiome knowledge accessible to everyone. They design
              probiotics based on human strains spread on food, with the goal of
              restoring a healthier microbiota.
              <br />
              <br />
              Marcus Creus studied biochemistry at Durham University and
              Cambridge. He is the chief scientific officer of Plusbiome AG.
              <br />
              <br />
              We discussed these three main topics:
            </p>
            mainTakeaways={mainTakeawaysCreus}
            fulltext=<p>
              <b>
                <h4>Processing</h4>
              </b>
              <br />
              During the lyophilization and encapsulation processes, two
              critical factors to consider are humidity and oxygen levels.
              <br />
              Achieving optimal lyophilization performance necessitates
              maintaining minimal humidity levels in the packaging facility.
              This is crucial to ensure an effective lyophilization process.
              <br />
              Additionally, it's essential to minimize oxygen exposure during
              these processes. When bacterial metabolism is halted, any presence
              of oxygen in the environment can trigger oxidation reactions,
              leading to product degradation and reduced viability. Hence, both
              the packaging and the processes should take place within a
              nitrogen atmosphere containing low oxygen concentrations.
              <br />
              <br />
              To enhance the stability of our protein, an alternative approach
              is to preliminary load them with other metals like magnesium or
              zinc. This strategy could potentially reduce the risk of
              deterioration following lyophilization. However, it is imperative
              to conduct thorough testing to validate its effectiveness in this
              regard.
              <br />
              <br />
              <b>
                <h4>Encapsulation</h4>
              </b>
              <br />
              Plusbiome does not use modified bacteria in its products, so it is
              not subject to regulation by food authorities. As we collaborate
              with both the European Medicines Agency (EMA) and the U.S. Food
              and Drug Administration (FDA) for regulatory compliance, they may
              have established standards for the encapsulation process.
              <br />
              <br />
              Plusbiome's product consists of 10 billion lyophilized bacteria
              enclosed within each stick. The packaging is composed of aluminium
              on the interior and plasticized paper on the exterior. The powder
              itself comprises lyophilized bacteria, maltodextrin, and silicon
              dioxide.
              <br />
              Maltodextrin serves the purpose of standardizing the quantity of
              live bacteria per unit weight of the product. It is chosen for its
              biocompatible characteristics.
              <br />
              Silicon dioxide plays a vital role as an anti-clumping agent. It
              absorbs moisture to maintain the powder's consistency and reduce
              the risk of deterioration.
              <br />
              <br />
              If we want to explore micro-encapsulation, using a sugar coating
              can significantly increase the bioavailability of the bacteria,
              potentially by a factor of three to four. Dosing three to four
              times more bacteria without encapsulation to achieve the same
              bacterial quantity in the intestine is also viable. We could
              conduct faecal analyses to assess the viability of the probiotic
              even when the bacteria are not encapsulated.
              <br />
              It's worth noting that Probiotical, based in Italy, specializes in
              micro-encapsulation techniques.
              <br />
              <br />
              For targeted delivery to specific segments of the intestine,
              various encapsulation methods are available, and companies like
              Lonza and Bacthera offer these services.
              <br />
              <br />
              <b>
                <h4>Regulation</h4>
              </b>
              <br />
              To secure bio-pharmaceutical approval, we must take several
              crucial steps:
              <br />
              <ula>
                <li>
                  Proof of Principle: The initial stage involves demonstrating
                  the viability of our product. This entails conducting
                  experiments in vitro (in a controlled laboratory setting) and
                  in mice to establish the concept's feasibility.{" "}
                </li>
                <li>
                  Preclinical Trials: Following the proof of principle,
                  preclinical trials are conducted to assess safety, efficacy,
                  and dosage parameters. These trials provide essential data
                  before moving to human testing.
                </li>
                <li>
                  Standard Clinical Trials: Clinical trials are typically
                  divided into two phases. The first phase involves a small
                  number of patients and focuses on safety and dosage. If
                  successful, it paves the way for the larger-scale phase two
                  trial.
                </li>
                <li>
                  Large-Scale Clinical Trial: In the second phase of clinical
                  trials, a more extensive patient population is involved to
                  gather more comprehensive data on effectiveness and safety.
                </li>
                <li>
                  Approval: Securing approval from the U.S. FDA and the EMA is a
                  rigorous and time-consuming process. It demands meticulous
                  documentation, comprehensive data, and adherence to strict
                  regulatory guidelines.
                </li>
              </ula>
              <br />
              <br />
              <b>
                <h4>Cost</h4>
              </b>
              <br />
              In addition to regulatory considerations, the proof of concept and
              clinical testing phases typically incur expenses ranging from $500
              million to $1 billion. When accounting for both research and
              production costs, projects like ours generally require an average
              investment of $3 billion before they can enter the market. <br />
              It's important to note that the production cost per dose is
              expected to be a relatively small fraction of the selling price,
              likely in the range of $10 to $100 per dose. <br />
              To recoup our research and development expenses, conducting a
              market study is necessary, as the selling price could potentially
              reach as high as $1,000 per dose. <br />
              <br />
              <b>
                <h4>Viability</h4>
              </b>
              <br />
              Based on the cost considerations, it appears that the product may
              not be intended for patients requiring chronic or long-term
              treatment but rather for emergency or life-saving situations.
              Additionally, it is worth noting that small molecules may be
              effective in capturing or binding to metal substances.
              <br />
              <br />
              <b>
                <h4>Conclusion</h4>
              </b>
              <br />
              <ula>
                <li>
                  To achieve successful lyophilization, it is crucial to
                  minimize both humidity and oxygen levels while incorporating
                  metal ions into our proteins. To obtain a high-quality powder,
                  we can enhance the process by adding maltodextrin and silicon
                  dioxide to the lyophilized bacteria.
                </li>
                <li>
                  While encapsulation may not be mandatory when the bacteria
                  concentration is sufficiently high, it can enhance the
                  specificity of delivery.
                </li>
                <li>
                  It is important to emphasize that adhering to FDA and EMA
                  regulations is imperative, and conducting preliminary tests
                  and clinical trials is a necessary part of the development
                  process.
                </li>
                <li>
                  In the current landscape, a product like ours may prove to be
                  too expensive for daily use, given the associated costs and
                  complexities involved.
                </li>
              </ula>
            </p>
          />
        </p>
        <br />
        <h2 className="mt-20" id="Biotech">
          Biotechnological aspect{" "}
        </h2>
        <hr className="dividergeneral"></hr>
        <p className="mb-2">
          <figure>
            <center>
              <img
                src="https://static.igem.wiki/teams/4888/wiki/humanpractices/product-conditionnement-1.png"
                style={{ paddingTop: "30px", width: "70%", height: "auto" }}
                alt="product"
              />
              <i>
                <figcaption>Potential product timeline</figcaption>
              </i>
            </center>
          </figure>
        </p>
        <p className="mb-2">
          <h4>Phase 1: Bacteria culture and extraction</h4>
          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>
              Freezing of the sample, the metabolism of bacteria stops
              <a href="#refproduct2">
                <sup>2,</sup>
              </a>
              <a href="#refproduct3">
                <sup>3</sup>
              </a>
            </li>
            <li>
              Sublimation, the water is removed from the sample without the
              bacteria restarting their metabolism.
            </li>
            <li>
              Desorption, final step to completely dehydrate the sample. We
              obtain a powder containing the bacteria in a dormant state.
            </li>
          </ul>
          <h4>Phase 2: Conditionnement</h4>
          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>
              Lyophilisation: in an environment with low humidity and nitrogen
              atmosphere, no oxygen.
              <a href="#refproduct4">
                <sup>4,</sup>
              </a>
              <a href="#refproduct5">
                <sup>5</sup>
              </a>
            </li>
            <li>
              Encapsulation: depending on the FDA regulation and the quantity of
              bacteria we provide
              <ul
                className="ml-12 mb-10"
                style={{ listStyleType: "disc", listStylePosition: "inside" }}
              >
                <li>
                  Add Maltodextrin and Silicium dioxide to the powder and
                  encapsulate the powder in a protective stick with aluminium
                  and plastic to protect the sample.
                </li>
                <li>
                  Microencapsulation in capsule such as Shellac+Wpi capsule, to
                  reach specificaly the intestine.
                  <a href="#refproduct6">
                    <sup>6</sup>
                  </a>
                </li>
              </ul>
            </li>
          </ul>
          <h4>Phase 3: Transport and delivery</h4>
          <p className="mb-2"> To ensure the product's safe and reliable delivery to consumers, we
          must establish a robust distribution and storage network. Read how we plan on doing that in the  
          <a href="#Target"> Target population and distrubition</a> section below.
           </p>
        </p>
        <br />
        <h2 className="mt-20" id="Approval">
          Pharmaceutical approval
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          For clinical approval we conceived this tests timeline, some of the
          tests were performed during iGEM and some should be performed in the
          future if the team decides to continue the project to transform it in
          a real product.
          <br></br>
          <figure>
            <center>
              <img
                src="https://static.igem.wiki/teams/4888/wiki/humanpractices/product-trials.png"
                style={{ paddingTop: "30px", width: "100%", height: "auto" }}
                alt="product"
              />
              <i>
                <figcaption>Potential product timeline</figcaption>
              </i>
            </center>
          </figure>
        </p>
        <div className="mb-2 mt-10">
          <h4 className="mb-2">Phase 1: Pre-trial test and proof of concept</h4>

          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>
              Culture of the bacteria in the lab with the right medium. Presence
              of IPTG and theophylline to product SpaC as well as EC20. At the
              end of the culture, incubation with metal to load the proteins.
              Extraction of the bacteria from the medium. Filtration and wash to
              remove a maximum of IPTG and theophylline.
            </li>
            <li>
              Mucus binding, intestine on a chip essay: The bacteria would be
              tested to verify that the protein SpaC is binding the mucus using
              an intestine of a chip design.
            </li>
            <li>
              Cadmium et zinc binding, colorimetric essay : The bacteria would
              be tested to quantify the affinity of the protein EC20 to cadmium
              and zinc and to quantify the amount of metals bound to the
              bacteria’s surface proteins. We would test several concentration
              and mixture of metal ions.
            </li>
            <li>
              HGT avoidance, dut/ung inactivation : Test the result of the
              interaction of our bacteria with other strain using a visible
              plasmid (ampicillin resistance, GFP...)
            </li>
            <li>
              Lyophilisation : test the lyophilisation process and redo all the
              test before and after rehydration in the right medium.
            </li>
          </ul>
          <h4 className="mb-2">Phase 2: Animal trials</h4>
          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>
              If possible, simulate the interaction between our bacteria and the
              microbiome and anticipate side effect. Fiest test on mice.
            </li>
            <li>
              Second test on pigs or minipigs since they are closer to humans.
            </li>
            <li>
              Test the interaction with the microbiota and long-term side
              effects. With animal tests on mice, we could see the effects on
              the microbiota during the treatment and after the treatment during
              the animal’s life. We could also test if there are alterations in
              the microbiota of the untreated second generation of mice born
              from treated parents.
            </li>
          </ul>
          <h4 className="mb-2">Phase 3: Clinical trials</h4>
          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>First clinical trials</li>
            <li>Second clinical trials</li>
            <li>Approval</li>
          </ul>
        </div>
        <br />
        <h2 className="mt-20" id="Cost">
          Prediction of the cost
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          In our product plan we investigate the economic cost of taking our
          product to market.
        </p>
        <p className="mb-2">
          We aimed at providing a solution for cadmium intoxication accessible
          by anyone who is affected. The cost and the distribution method are
          the biggest challenges to overcome. A low-cost probiotic could be
          bought by individuals and distributed by associations, national or
          regional healthcare systems and industries. Whereas a costly probiotic
          we would exclude a large part of the affected population and we would
          be forced to target bigger organizations as buyers and distributors.
        </p>
        <p className="mb-2">
          In accordance to the meeting with Marcus Creus, who works in
          biological therapeutics, taking our project to market will cost
          billions, especially for the clinical trials. The cost of production
          of the therapeutics and the cost of the research behind are only a
          small portion of the total cost.
        </p>
        <p className="mb-2">
          According to him, our product will cost between $1 and $100 for each
          pill, without the cost of clinical trials. It might exceed $1000 for
          the pill to be viable. The ideal use of our product is on a daily or
          two-days basis, making $1000 per pill far beyond the viable cost of
          our product.
        </p>
        <p className="mb-2">
          To reduce the price of our product, we should initially decide not to
          include the expenses associated with tests and clinical trials in the
          pricing. To achieve this, we would need to secure sponsors willing to
          invest in our technology without expecting any financial returns.
          Additionally, our product should seek grants from the government or
          organizations to further decrease the cost and enhance accessibility.
          In this regard, we are optimistic about securing funding from regional
          governments in areas most impacted by cadmium contamination, as well
          as from industries that would view it as an extra safety measure for
          their workforce. Through our efforts in human practices, we've
          observed significant interest in our project within the industrial
          sector. This encourages us to believe that we will eventually attract
          sponsors who can help offset a portion of the approval costs.{" "}
        </p>
        <p className="mb-2">
          Another step we can take is optimizing our production methods to
          minimize production costs. It is worth noting that these technologies
          are still very new, but as time progresses and our product gains
          approval, we can anticipate improvements in production efficiency and
          cost reduction. In addition, if the product was deployed the cost will
          decrease as it would be possible to produce it in large scale.
          Moreover the more costly part, excluding the clinical trials and
          approval, is the development of the modified strain. When the train
          will be stabilised, we could use the same strain for all the probiotic
          productions and sent it to other laboratories.
        </p>
        <br />
        <h2 className="mt-20" id="Target">
          Target population and distribution
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          Having decided to distribute our product in lyophilized form, meeting
          the necessary requirements should not present insurmountable
          challenges.
        </p>
        <br />
        <p className="mb-2">
          To ensure the product's safe and reliable delivery to consumers, we
          must establish a robust distribution and storage network. This entails
          vigilantly monitoring temperature and storage conditions during
          transportation and warehousing. To achieve this, it is imperative that
          we select dependable carriers and logistics partners with a proven
          track record in handling delicate products such as probiotics,
          especially in the case of GMO. These partners must be equipped to
          maintain the requisite temperature and humidity levels during transit.
          Additionally, employing suitable packaging materials to shield the
          lyophilized probiotic from moisture, light, and oxygen exposure is an
          essential precaution. The use of tamper-evident packaging can also
          provide consumers with added confidence in the product's safety. In
          terms of distribution, we must carefully choose our partners or
          channels, ensuring they possess the capabilities to meet our specified
          requirements. This may involve collaborating with pharmacies, health
          food stores, or online retailers.
        </p>
        <p className="mb-2">
          <br />
          Regarding storage requirements, we must adhere to several crucial
          factors
          <a href="#refproduct7">
            <sup>7</sup>
          </a>{" "}
          :
        </p>
        <p className="mb-2">
          <br />
          <ul
            className="ml-12 mb-10"
            style={{ listStyleType: "disc", listStylePosition: "inside" }}
          >
            <li>
              <mark>
                <b>Temperature control: </b>
              </mark>{" "}
              lyophilized probiotics often require specific temperature
              conditions for storage. Typically, they need to be stored in a
              cool, dry place away from direct sunlight. Refrigeration may be
              necessary for certain strains or formulations. We would better
              know the requirements for our LBP at the end of the lyophilization
              experiments.
            </li>{" "}
            <li>
              <mark>
                <b>Humidity control: </b>
              </mark>
              maintain appropriate humidity levels to prevent moisture
              absorption, which can degrade the product's quality. Moisture can
              lead to clumping or caking of the lyophilized powder.
            </li>
            <li>
              <mark>
                <b>Airtight containers: </b>
              </mark>{" "}
              store lyophilized probiotics in airtight containers to prevent
              exposure to oxygen, which can lead to oxidation and loss of
              potency.
            </li>
            <li>
              <mark>
                <b>Protection from light: </b>
              </mark>{" "}
              some probiotic strains are sensitive to light. Use opaque or
              tinted packaging, or store the product in dark, sealed containers
              to protect it from light exposure.
            </li>
            <li>
              <mark>
                <b>Segregation:</b>
              </mark>{" "}
              store lyophilized LBP separately from other products or substances
              that could potentially contaminate or affect the probiotic's
              quality.
            </li>
            <li>
              <mark>
                <b>Regular monitoring: </b>
              </mark>{" "}
              implement a monitoring system to track temperature and humidity
              conditions in storage areas. Regularly check for signs of
              deterioration, such as changes in color, texture, or odor.
            </li>
            <li>
              <mark>
                <b>First-In, First-Out (FIFO): </b>
              </mark>{" "}
              adhere to a FIFO inventory system to ensure that products with the
              earliest expiration dates are used or distributed first, reducing
              the risk of product spoilage.
            </li>
            <li>
              <mark>
                <b>Security </b>
              </mark>
              : implement security measures to prevent unauthorized access to
              the storage area, ensuring the safety of the product.
            </li>
          </ul>
          <br />
        </p>
        <p className="mb-2">
          Additionally, we have identified two primary target populations.{" "}
          <br />
          The first group comprises individuals residing in areas where
          agricultural soil is contaminated with cadmium, aiming to prevent the
          bioaccumulation of cadmium in their bodies. In these cases, it would
          be ideal for government entities or charitable organizations to supply
          our LBP. Alternatively, if feasible, we should strive to lower the
          cost of the LBP to make it accessible to individuals. <br />
          The second target population consists of workers exposed to cadmium in
          their workplaces. In such instances, the industry employing these
          workers should take direct responsibility for providing and covering
          the costs of the our product as an additional safety measure. In this
          second scenario, we maintain an optimistic view that numerous
          enterprises will acquire the LBP. This optimism stems from the fact
          that, as stated earlier in the pricing section and detailed in greater
          depth on our{" "}
          <a href="../epfl/humanpractices"> Human Practices page</a>, several
          leading industries have demonstrated substantial interest in our
          product.
        </p>
        <br />
        <h2 className="mt-20" id="references">
          {" "}
          References{" "}
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="refproduct1">
              {" "}
              Plusbiome compagny <br />{" "}
              <a href="https://plusbiome.com/"> Plusbiome AG </a>{" "}
            </li>
            <li id="refproduct2">
              {" "}
              Freeze-drying and cryopreservation of bacteria <br />{" "}
              <a href="https://www.proquest.com/docview/2786360046?fromopenview=true&pq-origsite=gscholar">
                {" "}
                Freeze-drying and cryopreservation of bacteria Perry, Stephen
                F.  Molecular Biotechnology; Totowa Vol. 9, N° 1, (Feb 1998):
                59-64.
              </a>{" "}
            </li>
            <li id="refproduct3">
              {" "}
              freeze-drying bacteria probiotics
              <br />{" "}
              <a href="https://books.google.ch/books?hl=en&lr=&id=TkdOCgAAQBAJ&oi=fnd&pg=PA213&dq=freeze-drying+bacteria+probiotics&ots=bDtIpwC_7o&sig=jIZ6AGyRg4O8PTMzZVVD9D0wDOo&redir_esc=y#v=onepage&q=freeze-drying%20bacteria%20probiotics&f=false">
                {" "}
                Freeze-drying bacteria probiotics Advances in Probiotic
                Technology,Petra ger, Chalat Santivarangkna CRC Press, 10 août
                2015{" "}
              </a>{" "}
            </li>
            <li id="refproduct4">
              {" "}
              Probiotic containing E.coli <br />{" "}
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5063008/#:~:text=coli%20bacteria%20are%20the%20basis,positive%20effects%20on%20human%20health">
                {" "}
                Insights from 100 Years of Research with Probiotic E. Coli Eur J
                Microbiol Immunol (Bp). 2016 Sep 29; 6(3): 147–161.
              </a>{" "}
            </li>

            <li id="refproduct5">
              {" "}
              Lyophilisation UK health Security Agency
              <br />{" "}
              <a href="https://www.culturecollections.org.uk/news/nctc-news/lyophilisation-long-term-storage-for-bacterial-strains.aspx#:~:text=The%20majority%20of%20bacterial%20strains,long%20term%20preservation%20of%20organisms">
                {" "}
                Lyophilisation: Long-term storage for bacterial and fungal
                strains UK health Security Agency, March 2017
              </a>{" "}
            </li>
            <li id="refproduct6">
              {" "}
              Shellac-WPI encapsulation
              <br />{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/32706117/">
                {" "}
                Microencapsulation of probiotic lactobacilli with shellac as
                moisture barrier and to allow controlled release J Sci Food
                Agric . 2021 Jan 30;101(2):726-734{" "}
              </a>{" "}
            </li>
            <li id="refproduct7">
              {" "}
              From culture to a probiotic
              <br />{" "}
              <a href="https://www.mdpi.com/2304-8158/11/15/2330">
                Current Trends in the Production of Probiotic Formulations Foods
                2022, 11(15), 2330; https://doi.org/10.3390/foods11152330{" "}
              </a>{" "}
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default ArticleProduct;
